import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router";

const SITE_URL = "https://chasingprovence.com";
const DEFAULT_OG_IMAGE = `${SITE_URL}/images/og-default.jpg`;
const SITE_NAME = "Chasing Provence";

interface PageMetaProps {
  title: string;
  description: string;
  /** Path to OG image (absolute URL or root-relative path) */
  image?: string;
  /** Set to true for pages that should not be indexed (404, unsubscribe, etc.) */
  noindex?: boolean;
  /** Override the canonical URL path (defaults to current pathname) */
  canonicalPath?: string;
  /** Override og:type (defaults to "website") */
  ogType?: string;
}

export default function PageMeta({
  title,
  description,
  image,
  noindex,
  canonicalPath,
  ogType,
}: PageMetaProps) {
  const { pathname } = useLocation();
  const canonical = `${SITE_URL}${canonicalPath ?? pathname}`;
  const ogImage = image
    ? image.startsWith("http")
      ? image
      : `${SITE_URL}${image}`
    : DEFAULT_OG_IMAGE;

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonical} />

      {noindex && <meta name="robots" content="noindex, follow" />}

      {/* Open Graph */}
      <meta property="og:type" content={ogType ?? "website"} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />
    </Helmet>
  );
}
